import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import type { OperatorReadOnlyShellDecision } from "@/integrations/multiresume/operator/route-guard";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { orpc } from "@/integrations/orpc/client";

import type { SearchType } from "./-components/operator-cockpit";

import { getOperatorShellServerFn } from "./-components/functions";
import { OperatorCockpit } from "./-components/operator-cockpit";
import { operatorTranslate } from "./-components/operator-i18n";

export const Route = createFileRoute("/operator/")({
  component: OperatorIndexPage,
  loader: async () => {
    return await getOperatorShellServerFn();
  },
});

function OperatorIndexPage() {
  const { i18n } = useLingui();
  const shellDecision = Route.useLoaderData();
  if (!shellDecision.allowed || !shellDecision.shell) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertTitle>{operatorTranslate(i18n, "Access Denied")}</AlertTitle>
          <AlertDescription>
            {operatorTranslate(
              i18n,
              "You do not have operator access. Please contact a supervisor if you believe this is an error.",
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <CockpitWithData
      shell={shellDecision.shell}
      onSearch={(query, type) => {
        const search = new URLSearchParams({ view: "user-detail", query, type: type ?? "email" });
        window.location.assign(`/operator/access?${search.toString()}`);
      }}
    />
  );
}

function CockpitWithData({
  shell,
  onSearch,
}: {
  shell: NonNullable<OperatorReadOnlyShellDecision["shell"]>;
  onSearch: (query: string, type?: SearchType) => void;
}) {
  const { i18n } = useLingui();
  const canReadCockpit = shell.permissions.includes("operator.read");
  const canReadAudit = shell.permissions.includes("operator.audit.read");
  const metricsQuery = useQuery({
    ...orpc.multiResumeOperator.cockpitMetrics.queryOptions({ input: {} }),
    enabled: canReadCockpit,
  });
  const recordsQuery = useQuery({
    ...orpc.multiResumeOperator.operationRecords.queryOptions({ input: { limit: 10 } }),
    enabled: canReadAudit,
  });
  const metricState = metricsQuery.isPending
    ? canReadCockpit
      ? "loading"
      : "unavailable"
    : !canReadCockpit || metricsQuery.isError || metricsQuery.data?.ok === false
      ? "unavailable"
      : "ready";
  const recentOperationsState = !canReadAudit
    ? "unavailable"
    : recordsQuery.isPending
      ? "loading"
      : recordsQuery.isError || recordsQuery.data?.ok === false
        ? "unavailable"
        : "ready";
  const metrics = metricsQuery.data?.ok ? metricsQuery.data.metrics : null;
  const recentOperations = useMemo(
    () =>
      recordsQuery.data?.ok
        ? recordsQuery.data.records.slice(0, 5).map((record) => ({
            label: operatorTranslate(i18n, operationActionLabel(record.action)),
            outcome: operatorTranslate(i18n, operationOutcomeLabel(record.result)),
            occurredAt: record.createdAt.toLocaleString(i18n.locale),
            actor: operatorTranslate(i18n, operationActorLabel(record.actor.role)),
            target: operatorTranslate(i18n, operationTargetLabel(record.target.type)),
          }))
        : [],
    [i18n, recordsQuery.data],
  );

  return (
    <OperatorCockpit
      abnormalCount={metrics?.unresolvedAbnormalCases ?? 0}
      openCaseCount={metrics?.quotaGrantFailures ?? 0}
      recentOperations={recentOperations}
      recentOperationsState={recentOperationsState}
      onSearch={onSearch}
      metrics={[
        {
          id: "affected-quota-grant-orders",
          label: operatorTranslate(i18n, "Quota grant failures"),
          unit: operatorTranslate(i18n, "Orders"),
          scope: operatorTranslate(i18n, "Persisted payment orders"),
          value: metrics?.quotaGrantFailures ?? null,
          state: metricState,
          href: "/operator/reconciliation",
          severity: "attention",
        },
        {
          id: "unresolved-abnormal-cases",
          label: operatorTranslate(i18n, "Unresolved abnormal cases"),
          unit: operatorTranslate(i18n, "Cases"),
          scope: operatorTranslate(i18n, "Open operator and abnormal cases"),
          value: metrics?.unresolvedAbnormalCases ?? null,
          state: metricState,
          href: "/operator/reconciliation",
          severity: "attention",
        },
        {
          label: operatorTranslate(i18n, "Total users"),
          scope: operatorTranslate(i18n, "All persisted users"),
          value: metrics?.totalUsers ?? null,
          state: metricState,
        },
        {
          label: operatorTranslate(i18n, "Activated users"),
          scope: operatorTranslate(i18n, "Verified and enabled users"),
          value: metrics?.activatedUsers ?? null,
          state: metricState,
        },
        {
          label: operatorTranslate(i18n, "Purchase-enabled users"),
          scope: operatorTranslate(i18n, "Active purchase whitelist"),
          value: metrics?.purchaseEnabledUsers ?? null,
          state: metricState,
        },
        {
          label: operatorTranslate(i18n, "Paid users"),
          scope: operatorTranslate(i18n, "Completed or paid orders"),
          value: metrics?.paidUsers ?? null,
          state: metricState,
        },
        {
          label: operatorTranslate(i18n, "Cumulative quota use"),
          scope: operatorTranslate(i18n, "Committed quota units"),
          value: metrics?.cumulativeQuotaUse ?? null,
          state: metricState,
        },
        {
          label: operatorTranslate(i18n, "Unused invites"),
          scope: operatorTranslate(i18n, "Active invite code capacity"),
          value: metrics?.unusedInvites ?? null,
          state: metricState,
        },
      ]}
    />
  );
}

function operationActionLabel(action: string): MessageDescriptor {
  const labels: Record<string, MessageDescriptor> = {
    "operator.invite.pause": msg`Pause invite`,
    "operator.invite.resume": msg`Resume invite`,
    "operator.invite.revoke": msg`Revoke invite`,
    "operator.user.disable": msg`Disable account`,
    "operator.user.restore": msg`Restore account`,
    "operator.purchase_whitelist.grant": msg`Grant purchase access`,
    "operator.purchase_whitelist.pause": msg`Pause purchase access`,
    "operator.purchase_whitelist.restore": msg`Restore purchase access`,
    "operator.purchase_whitelist.remove": msg`Remove purchase access`,
    "operator.quota_adjustment.support_grant": msg`Support quota grant`,
    "operator.quota_adjustment.manual_debit": msg`Manual quota debit`,
  };
  return labels[action] ?? msg`Operator operation`;
}

function operationOutcomeLabel(result: string): MessageDescriptor {
  return result === "committed" ? msg`Committed` : result === "rejected" ? msg`Rejected` : msg`Needs review`;
}

function operationActorLabel(role: string): MessageDescriptor {
  return role === "supervisor"
    ? msg`Supervisor`
    : role === "developer_incident_owner"
      ? msg`Developer incident owner`
      : msg`Operator`;
}

function operationTargetLabel(target: string): MessageDescriptor {
  const labels: Record<string, MessageDescriptor> = {
    user: msg`User target`,
    invite_code: msg`Invite code target`,
    order: msg`Order target`,
    operator_case: msg`Operator case target`,
    abnormal_event: msg`Abnormal event target`,
    auto_check_event: msg`Auto-check event target`,
  };
  return labels[target] ?? msg`Recorded target`;
}
